import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function MobilePayments() {
  const [accounts, setAccounts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('iban');
  const [paymentData, setPaymentData] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    description: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.get(`${API_URL}/accounts`, config);
      setAccounts(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/transactions`, {
        ...paymentData,
        amount: parseFloat(paymentData.amount),
        transaction_type: paymentMethod,
        currency: 'EUR',
        description: `${paymentMethod.toUpperCase()}: ${paymentData.description}`
      }, config);
      alert('Paiement effectué avec succès !');
      setPaymentData({ from_account: '', to_account: '', amount: '', description: '' });
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const paymentMethods = [
    { id: 'iban', icon: '🏦', name: 'IBAN/SEPA' },
    { id: 'visa', icon: '💳', name: 'Visa' },
    { id: 'mastercard', icon: '💳', name: 'Mastercard' },
    { id: 'paypal', icon: '💰', name: 'PayPal' },
    { id: 'western-union', icon: '🌍', name: 'Western Union' },
    { id: 'alipay', icon: '🇨🇳', name: 'Alipay' },
    { id: 'wechat', icon: '💬', name: 'WeChat Pay' },
    { id: 'epi', icon: '🇪🇺', name: 'EPI' },
    { id: 'qr-code', icon: '📱', name: 'QR Code' },
    { id: 'mobile', icon: '📞', name: 'Téléphone' }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Paiements Mobiles</h1>

      {/* Payment Methods */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Sélectionnez un mode de paiement</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {paymentMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => setPaymentMethod(method.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                paymentMethod === method.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              <div className="text-3xl mb-2">{method.icon}</div>
              <p className="font-semibold">{method.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Payment Form */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Envoyer de l'argent via {paymentMethods.find(m => m.id === paymentMethod)?.name || 'IBAN/SEPA'}
        </h2>
        <form onSubmit={handlePayment} className="space-y-4" key={paymentMethod}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Depuis le compte
            </label>
            <select
              value={paymentData.from_account}
              onChange={(e) => setPaymentData({ ...paymentData, from_account: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un compte</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.account_type} - {acc.balance.toFixed(2)} €
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {paymentMethod === 'iban' && 'IBAN destinataire'}
              {paymentMethod === 'paypal' && 'Email PayPal'}
              {paymentMethod === 'mobile' && 'Numéro de téléphone'}
              {!['iban', 'paypal', 'mobile'].includes(paymentMethod) && 'Identifiant destinataire'}
            </label>
            <input
              type="text"
              value={paymentData.to_account}
              onChange={(e) => setPaymentData({ ...paymentData, to_account: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={
                paymentMethod === 'iban' ? 'SC74 2004 1000 1234 5678 9012 3456 789' :
                paymentMethod === 'paypal' ? 'email@example.com' :
                paymentMethod === 'mobile' ? '+33 6 00 00 00 00' :
                'Identifiant'
              }
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant (€)
            </label>
            <input
              type="number"
              step="0.01"
              value={paymentData.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={paymentData.description}
              onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Motif du paiement"
            />
          </div>

          <button
            type="submit"
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Envoyer le paiement
          </button>
        </form>
      </div>

      {/* Info */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-2">🔒 Paiements sécurisés</h3>
        <p className="text-blue-800 text-sm">
          Tous les paiements sont chiffrés de bout en bout et conformes aux normes internationales de sécurité.
          Compatible avec toutes les banques : Crédit Agricole, CMB, Société Générale, Caisse d'Épargne, et plus.
        </p>
      </div>
    </div>
  );
}

export default MobilePayments;
